import React, { useState, useEffect } from 'react';
import { FileText, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

// Hooks
import {
  useIngredientes,
  useCategorias,
} from '../hooks/useIngredientes';
import { useCreateCategoria } from '../hooks/useCategorias';
import { useValidarDuplicado } from '../hooks/useFacturasCompra';
import { useFacturaSteps } from '../hooks/useFacturaSteps';
import { useFacturaTotales } from '../hooks/useFacturaTotales';
import { useUnidadesMedida, useCreateUnidadMedida } from '../hooks/useUnidadesMedida';

// Schemas y tipos
import {
  wizardFacturaSchema,
  type WizardFacturaFormData,
  normalizarRut,
} from '../schemas/facturaSchema';
import type { WizardFacturaData, FacturaOCRData, DetalleFacturaCompra, FacturaCompra } from '../types/facturas';

// Utilidades
import { capitalizarIngrediente, capitalizarMarca, inferirCategoriaIngrediente } from '../utils/facturaFormatters';
import { normalizarPrecioSinIva } from '../utils/facturaCalculos';

// Componentes
import UploadFacturaOCR from './UploadFacturaOCR';
import WizardStep1DatosFactura from './wizard/WizardStep1DatosFactura';
import WizardStep2Ingredientes from './wizard/WizardStep2Ingredientes';
import WizardStep3Resumen from './wizard/WizardStep3Resumen';
import WizardFooter from './wizard/WizardFooter';
import ModalVerificarIngredientes, {
  type ConflictoSimilar,
  type DecisionConflicto,
  type SimilarIngrediente,
} from './wizard/ModalVerificarIngredientes';
import { ingredientesService } from '../services/ingredientesService';

interface WizardFacturaCompraProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: WizardFacturaData) => Promise<void>;
  /** Cuando se proporciona, el wizard opera en modo edición */
  initialData?: FacturaCompra;
  /** Callback para guardar edición (en lugar de crear). Solo se usa cuando initialData está presente. */
  onUpdate?: (data: WizardFacturaData) => Promise<void>;
}

const WizardFacturaCompraNew: React.FC<WizardFacturaCompraProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  onUpdate,
}) => {
  const modoEdicion = !!initialData;
  // State local
  const [loading, setLoading] = useState(false);
  const [showUploadOCR, setShowUploadOCR] = useState(false);
  // Fix tanda1 H10 (2026-06-30): se removió el state zombie
  // `_showNuevoIngrediente`/`setShowNuevoIngrediente`. El botón "+ Nuevo"
  // ahora llama a handleAgregarFilaVacia (agrega fila vacía editable
  // inline, igual al flujo OCR).
  const [showNuevaCategoria, setShowNuevaCategoria] = useState(false);
  const [showNuevaUnidadMedida, setShowNuevaUnidadMedida] = useState(false);
  const [detalleIndexCategoria, setDetalleIndexCategoria] = useState<number | null>(null);
  const [detalleIndexUnidadMedida, setDetalleIndexUnidadMedida] = useState<number | null>(null);
  const [duplicadoWarning, setDuplicadoWarning] = useState<any>(null);
  const [showModalSimilares, setShowModalSimilares] = useState(false);
  const [conflictosSimilares, setConflictosSimilares] = useState<ConflictoSimilar[]>([]);
  const [nuevaCategoria, setNuevaCategoria] = useState({ nombre: '', descripcion: '' });
  const [nuevaUnidadMedida, setNuevaUnidadMedida] = useState({
    nombre: '',
    simbolo: '',
    tipo: 'cantidad' as 'peso' | 'volumen' | 'cantidad',
  });

  // Hooks de navegación y form
  const { pasoActual, siguiente, anterior, resetear, esPrimerPaso, esUltimoPaso } =
    useFacturaSteps(3);

  const form = useForm<WizardFacturaFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(wizardFacturaSchema as any),
    defaultValues: {
      numero_factura: '',
      proveedor_id: null,
      rut_proveedor: '',
      nombre_proveedor: '',
      fecha_emision: new Date().toISOString().split('T')[0],
      metodo_pago: 'efectivo',
      orden_compra_id: null,
      observaciones: '',
      valores_incluyen_iva: false,
      detalles: [],
      es_ocr: false,
    },
    mode: 'onChange',
  });

  // Hooks de datos
  // Fix tanda1 H10 (2026-06-30): limit alto (1000) para que el autocomplete
  // del Paso 2 vea TODOS los ingredientes y no solo la primera página
  // paginada. Antes era `useIngredientes()` sin args → backend devolvía la
  // 1ra página (default ~20) y el filtro `.includes(busqueda)` casi nunca
  // tenía match, por eso el listbox no desplegaba.
  const { data: ingredientesData } = useIngredientes({ limit: 1000 });
  const { data: categorias = [] } = useCategorias();
  const { data: unidadesMedida = [] } = useUnidadesMedida();
  const crearCategoriaMutation = useCreateCategoria();
  const crearUnidadMedidaMutation = useCreateUnidadMedida();
  const validarDuplicadoMutation = useValidarDuplicado();

  const ingredientes = ingredientesData?.ingredientes || [];

  // Watch form values
  const detalles = form.watch('detalles');
  const valoresIncluyenIva = form.watch('valores_incluyen_iva');
  const numeroFactura = form.watch('numero_factura');
  const rutProveedor = form.watch('rut_proveedor');

  // Calcular totales
  const totales = useFacturaTotales(detalles, valoresIncluyenIva);

  // Pre-poblar formulario cuando se abre en modo edición.
  // Se requiere que initialData.detalles exista para evitar un reset prematuro
  // con datos incompletos (por ejemplo, cuando initialData viene del listado sin detalles).
  useEffect(() => {
    if (isOpen && initialData && Array.isArray(initialData.detalles)) {
      form.reset({
        numero_factura: initialData.numero_factura,
        proveedor_id: initialData.proveedor_id,
        rut_proveedor: initialData.rut_proveedor,
        nombre_proveedor: initialData.nombre_proveedor,
        fecha_emision: initialData.fecha_emision
          ? initialData.fecha_emision.split('T')[0]
          : new Date().toISOString().split('T')[0],
        metodo_pago: initialData.metodo_pago,
        orden_compra_id: initialData.orden_compra_id,
        observaciones: initialData.observaciones || '',
        // Al cargar una factura para editar, los precios en BD están almacenados SIN IVA.
        // Se multiplican por 1.19 para que el usuario vea los valores TAL COMO APARECEN
        // en el documento físico (con IVA). Al guardar, el flag true hará que se dividan
        // por 1.19 y se almacenen nuevamente como valores netos.
        valores_incluyen_iva: true,
        detalles: (initialData.detalles || []).map((d) => {
          const IVA = 1.19;
          const cantidad = parseFloat(String(d.cantidad)) || 0;
          const precioUnitario = parseFloat(String(d.precio_unitario)) || 0;
          const subtotal = parseFloat(String(d.subtotal)) || 0;
          const jerarquiaNiveles = d.jerarquia_niveles != null ? Number(d.jerarquia_niveles) : 1;
          const esGrupo = jerarquiaNiveles >= 2;

          // precio_nivel1 = precio total del grupo (lo que el usuario ingresó originalmente).
          // Si no está guardado en BD (facturas antiguas), se reconstruye desde precio_pack
          // (campo legacy equivalente) o desde precio_unitario × cantidad.
          // precio_nivel1 ya contiene IVA (el usuario lo digitó desde el documento) → NO multiplicar.
          let precioNivel1 = d.precio_nivel1 != null ? parseFloat(String(d.precio_nivel1)) : null;
          if (precioNivel1 == null && esGrupo) {
            // Intentar recuperar desde precio_pack (campo legacy equivalente)
            if (d.precio_pack != null) {
              precioNivel1 = parseFloat(String(d.precio_pack));
            } else {
              // Última alternativa: precio_unitario × cantidad = precio total del grupo
              // En este caso sí se aplica IVA porque precio_unitario viene de BD (sin IVA)
              precioNivel1 = Math.round(precioUnitario * cantidad * IVA);
            }
          }

          return {
            ingrediente_id: d.ingrediente_id != null ? Number(d.ingrediente_id) : null,
            nombre_ingrediente_temporal:
              (d as any).ingrediente_nombre || d.nombre_ingrediente_temporal,
            marca: (d as any).ingrediente_marca || d.marca || null,
            categoria_nombre: (d as any).categoria || d.categoria_nombre || '',
            categoria_id: d.categoria_id != null ? Number(d.categoria_id) : null,
            // Conversión explícita a number: MySQL devuelve DECIMAL como string
            cantidad,
            unidad_medida: d.unidad_medida,
            // Multiplicar por IVA para mostrar el precio tal como aparece en el documento físico.
            // Al guardar, valores_incluyen_iva=true hará que normalizarPrecioSinIva divida por 1.19.
            // Se usan 2 decimales para preservar precisión y evitar acumulación de errores de redondeo.
            precio_unitario: Math.round(precioUnitario * IVA * 100) / 100,
            subtotal: Math.round(subtotal * IVA),
            // MySQL devuelve null por defecto para campos TEXT opcionales
            observaciones: d.observaciones ?? undefined,
            // MySQL devuelve TINYINT(1) como 0/1, convertir a boolean
            es_pack: Boolean(d.es_pack),
            cantidad_packs: d.cantidad_packs != null ? parseFloat(String(d.cantidad_packs)) : null,
            unidades_por_pack: d.unidades_por_pack != null ? parseFloat(String(d.unidades_por_pack)) : null,
            // precio_pack ya contiene IVA (campo legado, digitado directamente por usuario) → NO multiplicar
            precio_pack: d.precio_pack != null ? parseFloat(String(d.precio_pack)) : null,
            formato_compra: d.formato_compra ?? null,
            jerarquia_niveles: jerarquiaNiveles,
            nivel1_cantidad: d.nivel1_cantidad != null ? parseFloat(String(d.nivel1_cantidad)) : null,
            nivel1_nombre: d.nivel1_nombre ?? null,
            nivel1_unidades: d.nivel1_unidades != null ? parseFloat(String(d.nivel1_unidades)) : null,
            nivel2_nombre: d.nivel2_nombre ?? null,
            nivel2_cantidad_base: d.nivel2_cantidad_base != null ? parseFloat(String(d.nivel2_cantidad_base)) : null,
            // precio_nivel1: ya contiene IVA (digitado por usuario) → NO multiplicar
            precio_nivel1: precioNivel1,
            // precio_nivel2 viene de BD sin IVA → multiplicar (2 decimales para precisión)
            precio_nivel2: d.precio_nivel2 != null ? Math.round(parseFloat(String(d.precio_nivel2)) * IVA * 100) / 100 : null,
            // precio_base viene de BD sin IVA → multiplicar (2 decimales para precisión)
            precio_base: d.precio_base != null ? Math.round(parseFloat(String(d.precio_base)) * IVA * 100) / 100 : null,
            descripcion_jerarquia: d.descripcion_jerarquia ?? null,
          };
        }) as any,
        es_ocr: false,
      });
      resetear();
      setDuplicadoWarning(null);
    } else if (isOpen && !initialData) {
      // Modo creación: limpiar formulario si se abre sin datos previos
      form.reset({
        numero_factura: '',
        proveedor_id: null,
        rut_proveedor: '',
        nombre_proveedor: '',
        fecha_emision: new Date().toISOString().split('T')[0],
        metodo_pago: 'efectivo',
        orden_compra_id: null,
        observaciones: '',
        valores_incluyen_iva: false,
        detalles: [],
        es_ocr: false,
      });
      resetear();
      setDuplicadoWarning(null);
    }
  }, [isOpen, initialData]);

  // Validar duplicado (solo en modo creación, o si el número cambió respecto al original en edición)
  useEffect(() => {
    if (numeroFactura && rutProveedor) {
      // En modo edición: no validar si el número de factura no cambió
      if (modoEdicion && initialData && numeroFactura === initialData.numero_factura) {
        setDuplicadoWarning(null);
        return;
      }

      const timer = setTimeout(() => {
        const rutNormalizado = normalizarRut(rutProveedor);
        validarDuplicadoMutation.mutate(
          {
            numero_factura: numeroFactura,
            rut_proveedor: rutNormalizado,
          },
          {
            onSuccess: (data) => {
              setDuplicadoWarning(data.duplicado ? data : null);
            },
          }
        );
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [numeroFactura, rutProveedor]);

  // Handlers
  const handleClose = () => {
    form.reset();
    resetear();
    setDuplicadoWarning(null);
    setShowUploadOCR(false);
    onClose();
  };

  const handleOCRDataExtracted = (data: FacturaOCRData, imageUrl?: string) => {
    // Mapear datos del OCR al formulario
    form.setValue('numero_factura', data.numeroFactura || '');
    form.setValue('rut_proveedor', data.rutEmisor || '');
    form.setValue('nombre_proveedor', data.nombreProveedor || '');
    form.setValue('fecha_emision', data.fechaEmision || form.getValues('fecha_emision'));
    form.setValue('es_ocr', true);
    if (imageUrl) {
      form.setValue('imagen_url', imageUrl);
    }

    // Mapear items
    const detallesMapeados: DetalleFacturaCompra[] = data.items.map((item) => {
      const nombreCapitalizado = capitalizarIngrediente(item.nombreIngrediente);
      const categoriaInferida = inferirCategoriaIngrediente(nombreCapitalizado);
      const categoriaObj = categorias.find((c) => c.nombre === categoriaInferida);

      // Intentar mapear la unidad del OCR contra la tabla maestra de unidades.
      // El OCR puede devolver textos libres como "grs", "display", "unidades", etc.
      // Solo aceptamos la unidad si coincide exactamente con un símbolo del maestro
      // (comparación sin distinguir mayúsculas). Si no hay coincidencia, dejamos vacío
      // para que el usuario la seleccione manualmente desde el dropdown.
      const unidadOcr = item.unidadMedida?.trim().toLowerCase() || '';
      const unidadMaestro = (unidadesMedida as Array<{ id: number; simbolo: string; nombre: string }>).find(
        (u) => u.simbolo.toLowerCase() === unidadOcr || u.nombre.toLowerCase() === unidadOcr
      );
      const unidadResuelta = unidadMaestro ? unidadMaestro.simbolo : '';

      return {
        ingrediente_id: null,
        nombre_ingrediente_temporal: nombreCapitalizado,
        marca: item.marca ? capitalizarMarca(item.marca) : null,
        categoria_nombre: categoriaInferida,
        categoria_id: categoriaObj?.id || null,
        cantidad: item.cantidad,
        unidad_medida: unidadResuelta,
        precio_unitario: item.precioUnitario,
        subtotal: item.subtotal,
      };
    });

    form.setValue('detalles', detallesMapeados as any);
    setShowUploadOCR(false);
  };

  // Fix tanda1 H12 (2026-06-30): "Siguiente" avanzaba aunque el Paso 1
  // tuviera errores de validación (RUT inválido, número de factura
  // faltante). El `<input>` mostraba el mensaje pero el wizard
  // permitía pasar al Paso 2 igual. Ahora dispara `form.trigger()` con
  // los campos del paso actual antes de avanzar; si hay errores, queda
  // en el mismo paso (los mensajes de error ya están en el JSX).
  const handleSiguiente = async () => {
    if (pasoActual === 1) {
      const ok = await form.trigger([
        'numero_factura',
        'rut_proveedor',
        'nombre_proveedor',
        'fecha_emision',
        'metodo_pago',
      ]);
      if (!ok) return;
    }
    siguiente();
  };

  const handleRutChange = (rut: string) => {
    const rutNormalizado = normalizarRut(rut);
    // Auto-formatear el RUT
    const rutFormateado = formatearRutDisplay(rutNormalizado);
    form.setValue('rut_proveedor', rutFormateado, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const formatearRutDisplay = (rut: string): string => {
    if (rut.length < 2) return rut;
    const dv = rut.slice(-1);
    const numero = rut.slice(0, -1);
    const numeroFormateado = numero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${numeroFormateado}-${dv}`;
  };

  // Fix tanda1 H10 (2026-06-30): el botón "+ Nuevo" del Paso 2 antes
  // llamaba a setShowNuevoIngrediente(true) pero ningún render escuchaba
  // ese state (estaba prefijado con `_` por unused-var). Ahora agrega
  // una fila vacía al array `detalles` con ingrediente_id=null, igual
  // que el flujo OCR (ver handleOCRDataExtracted). El usuario completa
  // los campos inline y, al guardar, el handleSubmit busca similares y
  // crea el ingrediente nuevo si confirma. Consistente con el patrón
  // existente del wizard.
  const handleAgregarFilaVacia = () => {
    const detallesActuales = form.getValues('detalles');
    const nuevoDetalle: DetalleFacturaCompra = {
      ingrediente_id: null,
      nombre_ingrediente_temporal: '',
      cantidad: 1,
      unidad_medida: '',
      precio_unitario: 0,
      subtotal: 0,
    };
    form.setValue('detalles', [...detallesActuales, nuevoDetalle] as any);
  };

  const handleAgregarIngrediente = (ingredienteId: number) => {
    // Fix tanda1 H11 (2026-06-30): el adapter `adaptarIngredienteDesdeAPI`
    // (ingredientesService.ts:206) transforma el ingrediente del backend
    // (snake_case con `unidad_medida` string) a la forma del frontend
    // (camelCase con `unidadMedida` objeto). El handler venía leyendo
    // `ingrediente.unidad_medida` → undefined → "" → el detalle iba al
    // backend con `unidad_medida: ""` y la validación Zod respondía 400
    // "Datos inválidos". Ahora leemos `unidadMedida.abreviacion` (que es
    // el string corto que usa el backend) con fallback al ID descriptor.
    // Mismo problema afectaba `costo_unitario` (camelCase = `costoUnitario`).
    const ingrediente = (ingredientes as Array<{
      id: any;
      nombre: string;
      costoUnitario?: number;
      costo_unitario?: number;
      unidad_medida?: string;
      unidadMedidaId?: string;
      unidadMedida?: { abreviacion?: string; nombre?: string };
    }>).find((i) => String(i.id) === String(ingredienteId));
    if (!ingrediente) return;

    const costo =
      ingrediente.costoUnitario ?? ingrediente.costo_unitario ?? 0;
    const unidad =
      ingrediente.unidadMedida?.abreviacion ||
      ingrediente.unidadMedida?.nombre ||
      ingrediente.unidadMedidaId ||
      ingrediente.unidad_medida ||
      '';

    const precioNormalizado = normalizarPrecioSinIva(costo, valoresIncluyenIva);

    const nuevoDetalle: DetalleFacturaCompra = {
      ingrediente_id: Number(ingrediente.id),
      nombre_ingrediente_temporal: ingrediente.nombre,
      cantidad: 1,
      unidad_medida: unidad,
      precio_unitario: precioNormalizado,
      subtotal: precioNormalizado,
    };

    const detallesActuales = form.getValues('detalles');
    form.setValue('detalles', [...detallesActuales, nuevoDetalle] as any);
  };

  const handleCrearCategoria = async () => {
    if (!nuevaCategoria.nombre.trim()) {
      alert('El nombre de la categoría es requerido');
      return;
    }

    try {
      const nuevaCategoriaCreada = await crearCategoriaMutation.mutateAsync({
        nombre: nuevaCategoria.nombre.trim(),
        descripcion:
          nuevaCategoria.descripcion?.trim() ||
          `Categoría de ${nuevaCategoria.nombre.toLowerCase()}`,
      });

      console.log('✅ Categoría creada:', nuevaCategoriaCreada);

      if (detalleIndexCategoria !== null && nuevaCategoriaCreada) {
        handleUpdateDetalle(detalleIndexCategoria, 'categoria_nombre', nuevaCategoriaCreada.nombre);
        handleUpdateDetalle(detalleIndexCategoria, 'categoria_id', nuevaCategoriaCreada.id);
        console.log(`✅ Categoría "${nuevaCategoriaCreada.nombre}" asignada al detalle ${detalleIndexCategoria}`);
      }

      setNuevaCategoria({ nombre: '', descripcion: '' });
      setShowNuevaCategoria(false);
      setDetalleIndexCategoria(null);
    } catch (error) {
      console.error('❌ Error al crear categoría:', error);
      alert('Error al crear la categoría');
    }
  };

  const handleAbrirNuevaUnidadMedida = (index: number) => {
    setDetalleIndexUnidadMedida(index);
    setShowNuevaUnidadMedida(true);
  };

  const handleCrearUnidadMedida = async () => {
    try {
      setLoading(true);
      const result = await crearUnidadMedidaMutation.mutateAsync({
        nombre: nuevaUnidadMedida.nombre,
        simbolo: nuevaUnidadMedida.simbolo,
        tipo: nuevaUnidadMedida.tipo,
        activo: true,
      });

      // Actualizar el detalle con la nueva unidad
      if (detalleIndexUnidadMedida !== null) {
        handleUpdateDetalle(detalleIndexUnidadMedida, 'unidad_medida', result.simbolo);
      }

      // Resetear y cerrar
      setNuevaUnidadMedida({ nombre: '', simbolo: '', tipo: 'cantidad' });
      setShowNuevaUnidadMedida(false);
      setDetalleIndexUnidadMedida(null);
    } catch (error) {
      console.error('Error al crear unidad de medida:', error);
      alert('Error al crear la unidad de medida');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDetalle = (index: number, field: string, value: any) => {
    const detallesActuales = [...form.getValues('detalles')];
    (detallesActuales[index] as any)[field] = value;

    // Recalcular subtotal si cambió cantidad o precio
    if (field === 'cantidad' || field === 'precio_unitario') {
      detallesActuales[index].subtotal =
        detallesActuales[index].cantidad * detallesActuales[index].precio_unitario;
    }

    // Si cambió la unidad de medida y el item tiene agrupamiento activo,
    // regenerar la descripción de jerarquía con la nueva unidad real.
    if (field === 'unidad_medida') {
      const d = detallesActuales[index];
      const nivel = d.jerarquia_niveles || 1;
      const unidadReal = (value as string)?.trim() || '[unidad]';
      if (nivel === 1) {
        d.descripcion_jerarquia = `${d.cantidad} ${unidadReal}`;
      } else if (nivel === 2) {
        const n1qty = d.nivel1_cantidad || 1;
        const n1units = d.nivel1_unidades || 1;
        d.descripcion_jerarquia = `${n1qty} ${d.nivel1_nombre || 'Caja'} de ${n1units} ${unidadReal} c/u`;
      } else if (nivel === 3) {
        const n1qty = d.nivel1_cantidad || 1;
        const n1units = d.nivel1_unidades || 1;
        const n2base = d.nivel2_cantidad_base || 1;
        d.descripcion_jerarquia = `${n1qty} ${d.nivel1_nombre || 'Caja'} con ${n1units} ${d.nivel2_nombre || 'Bolsa'} de ${n2base} ${unidadReal} c/u`;
      }
    }

    form.setValue('detalles', detallesActuales);
  };

  const handleEliminarDetalle = (index: number) => {
    const detallesActuales = form.getValues('detalles');
    form.setValue(
      'detalles',
      detallesActuales.filter((_, i) => i !== index)
    );
  };

  // Construye el WizardFacturaData final y llama a onSubmit o onUpdate según el modo
  const submitFactura = async (detallesFinales: DetalleFacturaCompra[]) => {
    const formData = form.getValues();
    // Respetar siempre el flag del formulario.
    // En modo edición, valores_incluyen_iva=true fue activado al cargar (precios multiplicados × 1.19),
    // por lo que normalizarPrecioSinIva los dividirá por 1.19 y almacenará los valores netos correctamente.
    const incluyeIva = formData.valores_incluyen_iva;

    const dataParaEnviar: WizardFacturaData = {
      numero_factura: formData.numero_factura,
      proveedor_id: formData.proveedor_id != null ? Number(formData.proveedor_id) : null,
      rut_proveedor: normalizarRut(formData.rut_proveedor),
      nombre_proveedor: formData.nombre_proveedor,
      fecha_emision: formData.fecha_emision,
      metodo_pago: formData.metodo_pago,
      orden_compra_id: formData.orden_compra_id != null ? Number(formData.orden_compra_id) : null,
      observaciones: formData.observaciones || '',
      // Indicar al backend si los precios incluyen IVA para que aplique método SII
      valores_incluyen_iva: incluyeIva,
      detalles: detallesFinales.map((d) => {
        const precioBase = parseFloat(String(d.precio_unitario)) || 0;
        const cantidad = parseFloat(String(d.cantidad)) || 0;
        // Se envían los precios tal como el usuario los ingresó (brutos si incluyeIva=true,
        // netos si incluyeIva=false). El backend es quien normaliza a neto antes de guardar
        // y aplica el método SII para los totales agregados.
        return {
          ...d,
          ingrediente_id: d.ingrediente_id != null ? Number(d.ingrediente_id) : null,
          cantidad,
          precio_unitario: precioBase,
          subtotal: cantidad * precioBase,
          // Garantizar boolean puro para es_pack
          es_pack: Boolean(d.es_pack),
          // Garantizar numbers o null para campos numéricos opcionales
          cantidad_packs: d.cantidad_packs != null ? parseFloat(String(d.cantidad_packs)) : null,
          unidades_por_pack: d.unidades_por_pack != null ? parseFloat(String(d.unidades_por_pack)) : null,
          precio_pack: d.precio_pack != null ? parseFloat(String(d.precio_pack)) : null,
          jerarquia_niveles: d.jerarquia_niveles != null ? Number(d.jerarquia_niveles) : undefined,
          nivel1_cantidad: d.nivel1_cantidad != null ? parseFloat(String(d.nivel1_cantidad)) : null,
          nivel1_unidades: d.nivel1_unidades != null ? parseFloat(String(d.nivel1_unidades)) : null,
          nivel2_cantidad_base: d.nivel2_cantidad_base != null ? parseFloat(String(d.nivel2_cantidad_base)) : null,
          precio_nivel1: d.precio_nivel1 != null ? parseFloat(String(d.precio_nivel1)) : null,
          precio_nivel2: d.precio_nivel2 != null ? parseFloat(String(d.precio_nivel2)) : null,
          precio_base: d.precio_base != null ? parseFloat(String(d.precio_base)) : null,
          // observaciones null → undefined para que el backend lo omita correctamente
          observaciones: d.observaciones ?? undefined,
        };
      }),
      es_ocr: formData.es_ocr,
      imagen_url: formData.imagen_url,
    };

    if (modoEdicion && onUpdate) {
      await onUpdate(dataParaEnviar);
    } else {
      await onSubmit(dataParaEnviar);
    }
    handleClose();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formData = form.getValues();
      const detalles = formData.detalles;

      // Verificar similares solo para ingredientes nuevos (sin id asignado)
      const nuevos = detalles.filter((d) => !d.ingrediente_id);

      if (nuevos.length > 0) {
        const conflictos: ConflictoSimilar[] = [];

        for (const detalle of nuevos) {
          const nombre = detalle.nombre_ingrediente_temporal?.trim();
          if (!nombre) continue;

          try {
            const adaptados = await ingredientesService.buscarSimilares(nombre);

            if (adaptados.length > 0) {
              const similares: SimilarIngrediente[] = adaptados.map((ing) => ({
                id: parseInt(ing.id, 10),
                nombre: ing.nombre,
                categoria:
                  typeof ing.categoria === 'string' ? ing.categoria : 'Otros',
                stock_actual: ing.stockActual,
                activo: ing.activo,
                unidad_abreviacion: ing.unidadMedida?.abreviacion,
              }));

              conflictos.push({
                detalleIndex: detalles.indexOf(detalle),
                nombreBuscado: nombre,
                similares,
              });
            }
          } catch {
            // Si falla la búsqueda de similares, ignorar y continuar
          }
        }

        if (conflictos.length > 0) {
          setConflictosSimilares(conflictos);
          setShowModalSimilares(true);
          setLoading(false);
          return; // Esperar decisión del usuario
        }
      }

      // Sin conflictos: guardar directamente
      await submitFactura(detalles);
    } catch (error) {
      console.error('Error al guardar factura:', error);
    } finally {
      setLoading(false);
    }
  };

  // Aplica las decisiones del modal y guarda la factura
  const handleConfirmarConDecisiones = async (
    decisiones: Map<number, DecisionConflicto>
  ) => {
    setShowModalSimilares(false);
    setLoading(true);
    try {
      const detallesActuales = form.getValues('detalles');

      const detallesActualizados: DetalleFacturaCompra[] = detallesActuales.map(
        (detalle, index) => {
          const decision = decisiones.get(index);
          if (!decision) return detalle;

          if (decision.tipo === 'usar_existente') {
            // Vincular al ingrediente existente en lugar de crear uno nuevo
            return {
              ...detalle,
              ingrediente_id: decision.ingrediente.id,
              nombre_ingrediente_temporal: decision.ingrediente.nombre,
            };
          }
          // tipo === 'crear_nuevo': dejar como está (se creará nuevo)
          return detalle;
        }
      );

      form.setValue('detalles', detallesActualizados as any);
      await submitFactura(detallesActualizados);
    } catch (error) {
      console.error('Error al guardar factura con decisiones:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wizard-factura-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2
              id="wizard-factura-title"
              className="text-xl font-semibold text-gray-900 dark:text-gray-100"
            >
              {modoEdicion ? `Editar Factura de Compra` : 'Nueva Factura de Compra'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Cerrar wizard"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3].map((paso) => (
              <div key={paso} className="flex items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold
                    ${pasoActual >= paso ? 'bg-blue-600 dark:bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}
                  aria-current={pasoActual === paso ? 'step' : undefined}
                >
                  {paso}
                </div>
                {paso < 3 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${pasoActual > paso ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-4">
            <span>Datos Factura</span>
            <span>Ingredientes</span>
            <span>Resumen</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={(e) => e.preventDefault()}>
            {pasoActual === 1 && (
              <WizardStep1DatosFactura
                form={form}
                esOcr={form.watch('es_ocr') || false}
                duplicadoWarning={duplicadoWarning}
                onOpenUploadOCR={() => setShowUploadOCR(true)}
                onRutChange={handleRutChange}
                modoEdicion={modoEdicion}
              />
            )}

            {pasoActual === 2 && (
              <WizardStep2Ingredientes
                form={form}
                ingredientes={ingredientes}
                categorias={categorias}
                unidadesMedida={unidadesMedida}
                onUpdateDetalle={handleUpdateDetalle}
                onEliminarDetalle={handleEliminarDetalle}
                onAgregarIngrediente={handleAgregarIngrediente}
                onAbrirNuevoIngrediente={handleAgregarFilaVacia}
                onAbrirNuevaCategoria={(index) => {
                  setDetalleIndexCategoria(index);
                  setShowNuevaCategoria(true);
                }}
                onAbrirNuevaUnidadMedida={handleAbrirNuevaUnidadMedida}
              />
            )}

            {pasoActual === 3 && <WizardStep3Resumen form={form} totales={totales} />}
          </form>
        </div>

        {/* Footer */}
        <WizardFooter
          pasoActual={pasoActual}
          totalPasos={3}
          esPrimerPaso={esPrimerPaso}
          esUltimoPaso={esUltimoPaso}
          loading={loading}
          tieneDetalles={detalles.length > 0}
          onAnterior={anterior}
          onSiguiente={handleSiguiente}
          onSubmit={handleSubmit}
        />
      </div>

      {/* Modal Upload OCR */}
      <UploadFacturaOCR
        isOpen={showUploadOCR}
        onClose={() => setShowUploadOCR(false)}
        onDataExtracted={handleOCRDataExtracted}
      />

      {/* Modal Nueva Categoría */}
      {showNuevaCategoria && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Nueva Categoría de Ingrediente
              </h3>
              <button
                onClick={() => {
                  setShowNuevaCategoria(false);
                  setNuevaCategoria({ nombre: '', descripcion: '' });
                  setDetalleIndexCategoria(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nuevaCategoria.nombre}
                  onChange={(e) =>
                    setNuevaCategoria({ ...nuevaCategoria, nombre: e.target.value })
                  }
                  placeholder="ej: Especias, Enlatados, etc."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descripción (opcional)
                </label>
                <textarea
                  value={nuevaCategoria.descripcion}
                  onChange={(e) =>
                    setNuevaCategoria({ ...nuevaCategoria, descripcion: e.target.value })
                  }
                  placeholder="Descripción de la categoría"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowNuevaCategoria(false);
                  setNuevaCategoria({ nombre: '', descripcion: '' });
                  setDetalleIndexCategoria(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleCrearCategoria}
                disabled={!nuevaCategoria.nombre.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg
                         hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Crear Categoría
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nueva Unidad de Medida */}
      {showNuevaUnidadMedida && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Nueva Unidad de Medida
              </h3>
              <button
                onClick={() => {
                  setShowNuevaUnidadMedida(false);
                  setNuevaUnidadMedida({ nombre: '', simbolo: '', tipo: 'cantidad' });
                  setDetalleIndexUnidadMedida(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nuevaUnidadMedida.nombre}
                  onChange={(e) =>
                    setNuevaUnidadMedida({ ...nuevaUnidadMedida, nombre: e.target.value })
                  }
                  placeholder="Ej: Kilogramo, Litro, etc."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Símbolo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nuevaUnidadMedida.simbolo}
                  onChange={(e) =>
                    setNuevaUnidadMedida({ ...nuevaUnidadMedida, simbolo: e.target.value })
                  }
                  placeholder="Ej: kg, l, ml"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo <span className="text-red-500">*</span>
                </label>
                <select
                  value={nuevaUnidadMedida.tipo}
                  onChange={(e) =>
                    setNuevaUnidadMedida({
                      ...nuevaUnidadMedida,
                      tipo: e.target.value as 'peso' | 'volumen' | 'cantidad',
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="peso">Peso</option>
                  <option value="volumen">Volumen</option>
                  <option value="cantidad">Cantidad</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowNuevaUnidadMedida(false);
                  setNuevaUnidadMedida({ nombre: '', simbolo: '', tipo: 'cantidad' });
                  setDetalleIndexUnidadMedida(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleCrearUnidadMedida}
                disabled={!nuevaUnidadMedida.nombre || !nuevaUnidadMedida.simbolo || loading}
                className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg
                           hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de verificación de duplicados (pre-submit) */}
      <ModalVerificarIngredientes
        isOpen={showModalSimilares}
        conflictos={conflictosSimilares}
        onConfirmar={handleConfirmarConDecisiones}
        onCancelar={() => setShowModalSimilares(false)}
        loading={loading}
      />
    </div>
  );
};

export default WizardFacturaCompraNew;
